import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardHeader, Button, Collapse, CardBody, FormGroup, Label, Input, FormFeedback } from 'reactstrap';
import StatusCode from '../../../../../../util/StatusCode';
import Loading from '../../../../../misc/Loading';
import InstanceClient from '../../../../../../services/InstanceClient';
import { RequestState } from '../../../../../../models/RequestState';
import StorageContainerExplorer from '../../../../../storageContainer/StorageContainerExplorer';
import { CoreSDSv21CsvImportConfiguration } from '../../../../../../models/CoreSDSv21CsvImportModels';


export const validator = (configuration: CoreSDSv21CsvImportConfiguration, active: boolean) => {
    const validation: string[] = [];

    if (active) {
        if (!configuration.blobContainerUrl || configuration.blobContainerUrl === "") {
            validation.push('blobContainerUrl');
        }
        if (!configuration.blobSourceKey || configuration.blobSourceKey === "") {
            validation.push('blobSourceKey');
        }
    }

    return validation;
}

interface SDSv21CsvImportConfigurationProps {
    configuration: CoreSDSv21CsvImportConfiguration
    disabled: boolean
    updatePart: (part: Partial<CoreSDSv21CsvImportConfiguration>) => void
    active: boolean
    onValidChange: (arg: boolean) => void 
}


const SDSv21CsvImportConfiguration = (props: SDSv21CsvImportConfigurationProps) => {
    const { t } = useTranslation(['configuration', 'common']);

    const [open, setOpen] = useState<boolean>(true);
    const [expectedFiles, setExpectedFiles] = useState<RequestState<string[]>>({ code: StatusCode.PENDING })

    const validation = validator(props.configuration, props.active);

    useEffect(() => {
        props.onValidChange(validation.length ? true : false)
    }, [props.configuration, props.active]);

    useEffect(() => {
        InstanceClient.get<string[]>('/api/core/sdscsvimportconfiguration/requiredfiles')
            .then(res => 
                setExpectedFiles({
                    code: StatusCode.COMPLETE,
                    data: res.data
                }))
            .catch(error => 
                setExpectedFiles({
                    code: StatusCode.COMPLETE,
                    data: []
                }));
    }, [expectedFiles.code !== StatusCode.COMPLETE]);

    if (expectedFiles.code === StatusCode.COMPLETE) {
        return (
            <Card className="edit_module">
                <CardHeader tag="h2">
                    <Button outline color="secondary" style={{ width: '37px', float: 'right' }} onClick={() => setOpen(!open)}>{open ? '-' : '+'}</Button>
                    {t('modules.SDSv21CsvImport.title', { ns: 'common' })}
                </CardHeader>
                <Collapse isOpen={open}>
                    <CardBody>
                        <div style={{ margin: '1rem 0' }}>
                            <FormGroup>
                                <h5>{t('sdsCsvImport.access.title', { ns: 'configuration' })}</h5>
                                <FormGroup>
                                    <Label for="blobContainerUrl">{t('sdsCsvImport.access.linkContainer', { ns: 'configuration' })}</Label>
                                    <Input
                                        type="text"
                                        name="blobContainerUrl"
                                        id="blobContainerUrl"
                                        placeholder={t('sdsCsvImport.access.linkContainer', { ns: 'configuration' })}
                                        disabled={props.disabled}
                                        value={props.configuration.blobContainerUrl || ''}
                                        onChange={e => props.updatePart({ blobContainerUrl: e.target.value })}
                                        invalid={validation.indexOf('blobContainerUrl') !== -1}
                                        autoComplete="off"
                                    />
                                    <FormFeedback>{t('sdsCsvImport.access.feedbackLinkContainer', { ns: 'configuration' })}</FormFeedback>
                                </FormGroup>
                                <FormGroup>
                                    <Label for="blobSourceKey">{t('sdsCsvImport.access.blobSourceKey', { ns: 'configuration' })}</Label>
                                    <Input
                                        type="text"
                                        name="blobSourceKey"
                                        id="blobSourceKey"
                                        placeholder={t('sdsCsvImport.access.blobSourceKey', { ns: 'configuration' })}
                                        value={props.configuration.blobSourceKey || ''}
                                        disabled={props.disabled}
                                        onChange={e => props.updatePart({ blobSourceKey: e.target.value })}
                                        invalid={validation.indexOf('blobSourceKey') !== -1}
                                        autoComplete="off"
                                    />
                                    <FormFeedback>{t('sdsCsvImport.access.feedbackBlobSourceKey', { ns: 'configuration' })}</FormFeedback>
                                </FormGroup>
                                <StorageContainerExplorer
                                    blobContainerUrl={props.configuration.blobContainerUrl}
                                    blobSourceKey={props.configuration.blobSourceKey}
                                    expectedFiles={expectedFiles.data} />
                            </FormGroup>
                        </div>
                    </CardBody>
                </Collapse>
            </Card>);
    } else {
        return <Loading />
    }

}

export default SDSv21CsvImportConfiguration;
